import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Tags, Copy, Eye, Code2, Globe, Image, Share2 } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface MetaTags {
  title: string;
  description: string;
  keywords: string;
  author: string;
  canonical: string;
  robots: string;
  viewport: string;
  charset: string;
  language: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
  ogType: string;
  twitterCard: string;
  twitterSite: string;
  twitterCreator: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
}

const MetaTagGenerator: React.FC = () => {
  const [metaTags, setMetaTags] = useState<MetaTags>({
    title: '',
    description: '',
    keywords: '',
    author: '',
    canonical: '',
    robots: 'index, follow',
    viewport: 'width=device-width, initial-scale=1.0',
    charset: 'UTF-8',
    language: 'en',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    ogUrl: '',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterSite: '',
    twitterCreator: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: ''
  });

  const [generatedHtml, setGeneratedHtml] = useState('');
  const [activeTab, setActiveTab] = useState<'basic' | 'social' | 'advanced'>('basic');

  useSEO({
    title: 'Meta Tag Generator - Create SEO Optimized HTML Meta Tags',
    description: 'Generate complete HTML meta tags for SEO optimization. Create Open Graph, Twitter Cards, and standard meta tags to improve search engine rankings.',
    keywords: 'meta tag generator, SEO meta tags, HTML meta tags, open graph tags, twitter cards, SEO optimization',
    canonical: 'https://snap-tools.online/seo/meta-tag-generator'
  });

  // Update meta tag field
  const updateMetaTag = (field: keyof MetaTags, value: string) => {
    setMetaTags(prev => ({ ...prev, [field]: value }));
  };

  // Auto-fill social media fields from basic fields
  const autoFillSocial = () => {
    setMetaTags(prev => ({
      ...prev,
      ogTitle: prev.ogTitle || prev.title,
      ogDescription: prev.ogDescription || prev.description,
      twitterTitle: prev.twitterTitle || prev.title,
      twitterDescription: prev.twitterDescription || prev.description,
      twitterImage: prev.twitterImage || prev.ogImage
    }));
    toast.success('Social media fields auto-filled from basic information');
  };

  // Generate HTML meta tags
  const generateMetaTags = () => {
    let html = '';

    // Basic meta tags
    if (metaTags.charset) html += `<meta charset="${metaTags.charset}">\n`;
    if (metaTags.viewport) html += `<meta name="viewport" content="${metaTags.viewport}">\n`;
    if (metaTags.title) html += `<title>${metaTags.title}</title>\n`;
    if (metaTags.description) html += `<meta name="description" content="${metaTags.description}">\n`;
    if (metaTags.keywords) html += `<meta name="keywords" content="${metaTags.keywords}">\n`;
    if (metaTags.author) html += `<meta name="author" content="${metaTags.author}">\n`;
    if (metaTags.language) html += `<meta name="language" content="${metaTags.language}">\n`;
    if (metaTags.robots) html += `<meta name="robots" content="${metaTags.robots}">\n`;
    if (metaTags.canonical) html += `<link rel="canonical" href="${metaTags.canonical}">\n`;

    // Open Graph tags
    html += '\n<!-- Open Graph / Facebook -->\n';
    if (metaTags.ogType) html += `<meta property="og:type" content="${metaTags.ogType}">\n`;
    if (metaTags.ogUrl) html += `<meta property="og:url" content="${metaTags.ogUrl}">\n`;
    if (metaTags.ogTitle) html += `<meta property="og:title" content="${metaTags.ogTitle}">\n`;
    if (metaTags.ogDescription) html += `<meta property="og:description" content="${metaTags.ogDescription}">\n`;
    if (metaTags.ogImage) html += `<meta property="og:image" content="${metaTags.ogImage}">\n`;

    // Twitter Card tags
    html += '\n<!-- Twitter -->\n';
    if (metaTags.twitterCard) html += `<meta property="twitter:card" content="${metaTags.twitterCard}">\n`;
    if (metaTags.twitterSite) html += `<meta property="twitter:site" content="${metaTags.twitterSite}">\n`;
    if (metaTags.twitterCreator) html += `<meta property="twitter:creator" content="${metaTags.twitterCreator}">\n`;
    if (metaTags.twitterTitle) html += `<meta property="twitter:title" content="${metaTags.twitterTitle}">\n`;
    if (metaTags.twitterDescription) html += `<meta property="twitter:description" content="${metaTags.twitterDescription}">\n`;
    if (metaTags.twitterImage) html += `<meta property="twitter:image" content="${metaTags.twitterImage}">\n`;

    setGeneratedHtml(html);
    toast.success('Meta tags generated successfully');
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    if (!generatedHtml) {
      toast.error('No meta tags to copy. Please generate them first.');
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedHtml);
      toast.success('Meta tags copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy meta tags');
    }
  };

  // Reset form
  const resetForm = () => {
    setMetaTags({
      title: '',
      description: '',
      keywords: '',
      author: '',
      canonical: '',
      robots: 'index, follow',
      viewport: 'width=device-width, initial-scale=1.0',
      charset: 'UTF-8',
      language: 'en',
      ogTitle: '',
      ogDescription: '',
      ogImage: '',
      ogUrl: '',
      ogType: 'website',
      twitterCard: 'summary_large_image',
      twitterSite: '',
      twitterCreator: '',
      twitterTitle: '',
      twitterDescription: '',
      twitterImage: ''
    });
    setGeneratedHtml('');
    toast.success('Form reset successfully');
  };

  // Load example data
  const loadExample = () => {
    setMetaTags({
      title: 'Amazing Product - Best Solution for Your Needs',
      description: 'Discover our amazing product that solves all your problems. Get started today with our comprehensive solution designed for modern businesses.',
      keywords: 'amazing product, solution, business, modern, comprehensive',
      author: 'Your Company Name',
      canonical: 'https://example.com/amazing-product',
      robots: 'index, follow',
      viewport: 'width=device-width, initial-scale=1.0',
      charset: 'UTF-8',
      language: 'en',
      ogTitle: 'Amazing Product - Best Solution for Your Needs',
      ogDescription: 'Discover our amazing product that solves all your problems. Get started today with our comprehensive solution designed for modern businesses.',
      ogImage: 'https://example.com/images/amazing-product-og.jpg',
      ogUrl: 'https://example.com/amazing-product',
      ogType: 'website',
      twitterCard: 'summary_large_image',
      twitterSite: '@yourcompany',
      twitterCreator: '@yourcompany',
      twitterTitle: 'Amazing Product - Best Solution for Your Needs',
      twitterDescription: 'Discover our amazing product that solves all your problems. Get started today with our comprehensive solution.',
      twitterImage: 'https://example.com/images/amazing-product-twitter.jpg'
    });
    toast.success('Example data loaded');
  };

  const tabs = [
    { id: 'basic', label: 'Basic Meta Tags', icon: Tags },
    { id: 'social', label: 'Social Media', icon: Share2 },
    { id: 'advanced', label: 'Advanced', icon: Code2 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Tags className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Meta Tag Generator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Generate comprehensive HTML meta tags for SEO optimization. Create Open Graph tags, Twitter Cards, and standard meta tags to improve your website's search engine visibility and social media presence.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Action Buttons */}
          <div className="flex gap-3 justify-center mb-8 flex-wrap">
            <button
              onClick={loadExample}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Eye className="w-4 h-4" />
              Load Example
            </button>
            <button
              onClick={autoFillSocial}
              className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Auto-fill Social
            </button>
            <button
              onClick={generateMetaTags}
              className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Code2 className="w-4 h-4" />
              Generate Tags
            </button>
            <button
              onClick={resetForm}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reset Form
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="flex bg-gray-100 rounded-lg p-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center gap-2 py-2 px-4 rounded-md transition-colors ${
                      activeTab === tab.id 
                        ? 'bg-white text-purple-600 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Form Content */}
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Input Form */}
            <div className="space-y-6">
              {activeTab === 'basic' && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-4"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Basic Meta Tags</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Page Title *
                    </label>
                    <input
                      type="text"
                      value={metaTags.title}
                      onChange={(e) => updateMetaTag('title', e.target.value)}
                      placeholder="Enter page title (50-60 characters recommended)"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      maxLength={60}
                    />
                    <div className="text-xs text-gray-500 mt-1">{metaTags.title.length}/60 characters</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta Description *
                    </label>
                    <textarea
                      value={metaTags.description}
                      onChange={(e) => updateMetaTag('description', e.target.value)}
                      placeholder="Enter meta description (150-160 characters recommended)"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows={3}
                      maxLength={160}
                    />
                    <div className="text-xs text-gray-500 mt-1">{metaTags.description.length}/160 characters</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Keywords
                    </label>
                    <input
                      type="text"
                      value={metaTags.keywords}
                      onChange={(e) => updateMetaTag('keywords', e.target.value)}
                      placeholder="Enter keywords separated by commas"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Author
                    </label>
                    <input
                      type="text"
                      value={metaTags.author}
                      onChange={(e) => updateMetaTag('author', e.target.value)}
                      placeholder="Enter author name"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Canonical URL
                    </label>
                    <input
                      type="url"
                      value={metaTags.canonical}
                      onChange={(e) => updateMetaTag('canonical', e.target.value)}
                      placeholder="https://example.com/page"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </motion.div>
              )}

              {activeTab === 'social' && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Open Graph Tags</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OG Title
                    </label>
                    <input
                      type="text"
                      value={metaTags.ogTitle}
                      onChange={(e) => updateMetaTag('ogTitle', e.target.value)}
                      placeholder="Open Graph title"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OG Description
                    </label>
                    <textarea
                      value={metaTags.ogDescription}
                      onChange={(e) => updateMetaTag('ogDescription', e.target.value)}
                      placeholder="Open Graph description"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OG Image URL
                    </label>
                    <input
                      type="url"
                      value={metaTags.ogImage}
                      onChange={(e) => updateMetaTag('ogImage', e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-8">Twitter Cards</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter Site
                    </label>
                    <input
                      type="text"
                      value={metaTags.twitterSite}
                      onChange={(e) => updateMetaTag('twitterSite', e.target.value)}
                      placeholder="@username"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter Title
                    </label>
                    <input
                      type="text"
                      value={metaTags.twitterTitle}
                      onChange={(e) => updateMetaTag('twitterTitle', e.target.value)}
                      placeholder="Twitter card title"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter Image
                    </label>
                    <input
                      type="url"
                      value={metaTags.twitterImage}
                      onChange={(e) => updateMetaTag('twitterImage', e.target.value)}
                      placeholder="https://example.com/twitter-image.jpg"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </motion.div>
              )}

              {activeTab === 'advanced' && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-4"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Advanced Settings</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Robots
                    </label>
                    <select
                      value={metaTags.robots}
                      onChange={(e) => updateMetaTag('robots', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="index, follow">index, follow</option>
                      <option value="noindex, follow">noindex, follow</option>
                      <option value="index, nofollow">index, nofollow</option>
                      <option value="noindex, nofollow">noindex, nofollow</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OG Type
                    </label>
                    <select
                      value={metaTags.ogType}
                      onChange={(e) => updateMetaTag('ogType', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="website">website</option>
                      <option value="article">article</option>
                      <option value="product">product</option>
                      <option value="profile">profile</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter Card Type
                    </label>
                    <select
                      value={metaTags.twitterCard}
                      onChange={(e) => updateMetaTag('twitterCard', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="summary">summary</option>
                      <option value="summary_large_image">summary_large_image</option>
                      <option value="app">app</option>
                      <option value="player">player</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Language
                    </label>
                    <input
                      type="text"
                      value={metaTags.language}
                      onChange={(e) => updateMetaTag('language', e.target.value)}
                      placeholder="en"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </motion.div>
              )}
            </div>

            {/* Generated Output */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Generated Meta Tags</h3>
                  <button
                    onClick={copyToClipboard}
                    disabled={!generatedHtml}
                    className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    Copy HTML
                  </button>
                </div>
                
                <div className="bg-white rounded-lg border p-4 h-96 overflow-y-auto">
                  {generatedHtml ? (
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                      {generatedHtml}
                    </pre>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <Code2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>Fill in the form and click "Generate Tags" to see the HTML output</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Preview Cards */}
              {metaTags.title && metaTags.description && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-blue-50 rounded-lg p-6"
                >
                  <h4 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Search Result Preview
                  </h4>
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h3 className="text-blue-600 text-lg hover:underline cursor-pointer">
                      {metaTags.title}
                    </h3>
                    <div className="text-green-700 text-sm mt-1">{metaTags.canonical || 'https://example.com'}</div>
                    <p className="text-gray-600 text-sm mt-2">{metaTags.description}</p>
                  </div>
                </motion.div>
              )}

              {metaTags.ogTitle && metaTags.ogDescription && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-blue-50 rounded-lg p-6"
                >
                  <h4 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                    <Share2 className="w-5 h-5" />
                    Social Media Preview
                  </h4>
                  <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                    {metaTags.ogImage && (
                      <div className="h-48 bg-gray-200 flex items-center justify-center">
                        <Image className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900">{metaTags.ogTitle}</h3>
                      <p className="text-gray-600 text-sm mt-1">{metaTags.ogDescription}</p>
                      <div className="text-gray-500 text-xs mt-2">{metaTags.ogUrl || 'example.com'}</div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">SEO Best Practices</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Title Length:</strong> Keep titles between 50-60 characters</li>
                <li>• <strong>Description Length:</strong> Aim for 150-160 characters</li>
                <li>• <strong>Unique Content:</strong> Each page should have unique meta tags</li>
                <li>• <strong>Keywords:</strong> Include relevant keywords naturally</li>
                <li>• <strong>Call to Action:</strong> Make descriptions compelling</li>
                <li>• <strong>Mobile-First:</strong> Ensure viewport meta tag is included</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Social Media Optimization</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Image Size:</strong> Use 1200x630px for optimal display</li>
                <li>• <strong>Consistent Branding:</strong> Match your brand voice and style</li>
                <li>• <strong>Test Previews:</strong> Use Facebook and Twitter debugging tools</li>
                <li>• <strong>Alt Text:</strong> Include descriptive alt text for images</li>
                <li>• <strong>Multiple Formats:</strong> Create different sizes for different platforms</li>
                <li>• <strong>Update Regularly:</strong> Keep social tags current with content</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MetaTagGenerator;
